import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTag,
  faCheckCircle,
  faEye,
  faEdit,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { getStatusBadge } from './tagStatusHelper';

/**
 * Componente individual de tarjeta de incidencia en el catálogo
 */
const TagCard = ({
  tag,
  isDark,
  onOpenView,
  onOpenEdit,
  onOpenComplete,
  onOpenDelete
}) => {
  const statusInfo = getStatusBadge(tag.iEstadoIncidencia, isDark);

  return (
    <div className="col-12 col-md-6 col-lg-4">
      <div
        className="p-3 rounded-3 border h-100 d-flex flex-column justify-content-between transition-all"
        style={{
          backgroundColor: isDark ? '#142230' : '#f8fafc',
          borderColor: isDark ? '#233142' : '#e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
          cursor: 'pointer'
        }}
        onClick={() => onOpenView(tag)}
      >
        <div>
          {/* Header de la tarjeta con Badges y Acciones */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-1 flex-wrap" style={{ maxWidth: 'calc(100% - 75px)' }}>
              <span
                className="badge px-3 py-1 rounded-pill fw-semibold d-inline-flex align-items-center"
                style={{
                  backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                  color: isDark ? '#cbd5e1' : '#334155',
                  border: `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                  fontSize: '0.82rem',
                  gap: '6px',
                  overflow: 'hidden'
                }}
                title={tag.vNombre}
              >
                <FontAwesomeIcon icon={faTag} className="me-1" style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tag.vNombre}
                </span>
              </span>

              <span
                className="badge px-2 py-0.5 rounded-pill fw-semibold d-inline-flex align-items-center gap-1"
                style={{
                  backgroundColor: statusInfo.bg,
                  color: statusInfo.color,
                  border: `1px solid ${statusInfo.border}`,
                  fontSize: '0.72rem'
                }}
              >
                <FontAwesomeIcon icon={statusInfo.icon} className="me-1" style={{ fontSize: '0.65rem' }} />
                <span>{statusInfo.label}</span>
              </span>
            </div>

            <div className="d-flex align-items-center gap-1" onClick={e => e.stopPropagation()}>
              {Number(tag.iEstadoIncidencia) !== 3 && Number(tag.iTotalTickets) > 0 && (
                <button
                  type="button"
                  className="btn btn-sm p-1 text-success border-0 bg-transparent hover-opacity"
                  title="Resolver incidencia"
                  onClick={() => onOpenComplete(tag)}
                >
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '0.88rem' }} />
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm p-1 text-primary border-0 bg-transparent hover-opacity"
                title="Ver detalle de incidencia"
                onClick={() => onOpenView(tag)}
              >
                <FontAwesomeIcon icon={faEye} style={{ fontSize: '0.85rem' }} />
              </button>
              <button
                type="button"
                className="btn btn-sm p-1 text-muted border-0 bg-transparent hover-opacity"
                title="Editar incidencia"
                onClick={() => onOpenEdit(tag)}
              >
                <FontAwesomeIcon icon={faEdit} style={{ fontSize: '0.85rem' }} />
              </button>
              <button
                type="button"
                className="btn btn-sm p-1 text-danger border-0 bg-transparent hover-opacity"
                title="Eliminar incidencia"
                onClick={() => onOpenDelete(tag)}
              >
                <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.85rem' }} />
              </button>
            </div>
          </div>

          {/* Descripción y notas */}
          <div
            className="small mb-0"
            style={{
              fontSize: '0.84rem',
              color: isDark ? '#94a3b8' : '#64748b',
              minHeight: '2.4em',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '120px',
              overflowY: 'auto'
            }}
          >
            {tag.vDescripcion || <span className="fst-italic opacity-50">Sin notas o descripción</span>}
          </div>
        </div>

        {/* Contadores: Tickets vinculados y Fotos adjuntas (Informativos) */}
        <div className="d-flex align-items-center gap-2 my-2">
          <div
            className="d-flex align-items-center justify-content-between flex-grow-1 py-1.5 px-2 rounded-2"
            style={{
              backgroundColor: isDark ? '#1a2936' : '#f1f5f9',
              fontSize: '0.78rem'
            }}
          >
            <div className="d-flex align-items-center gap-1.5 text-muted">
              <i className="bi bi-ticket-perforated me-1" style={{ fontSize: '0.85rem', color: '#6366f1' }} />
              <span>Tickets:</span>
            </div>
            <span
              className="badge rounded-pill fw-bold"
              style={{
                backgroundColor: (tag.iTotalTickets || 0) > 0 ? '#6366f1' : (isDark ? '#334155' : '#cbd5e1'),
                color: '#ffffff',
                fontSize: '0.72rem'
              }}
            >
              {tag.iTotalTickets || 0}
            </span>
          </div>

          <div
            className="d-flex align-items-center justify-content-between flex-grow-1 py-1.5 px-2 rounded-2"
            style={{
              backgroundColor: isDark ? '#1a2936' : '#f1f5f9',
              fontSize: '0.78rem'
            }}
          >
            <div className="d-flex align-items-center gap-1.5 text-muted">
              <i className="bi bi-image me-1" style={{ fontSize: '0.85rem', color: '#0ea5e9' }} />
              <span>Fotos:</span>
            </div>
            <span
              className="badge rounded-pill fw-bold"
              style={{
                backgroundColor: (tag.iTotalFotos || 0) > 0 ? '#0ea5e9' : (isDark ? '#334155' : '#cbd5e1'),
                color: '#ffffff',
                fontSize: '0.72rem'
              }}
            >
              {tag.iTotalFotos || 0}
            </span>
          </div>
        </div>

        {/* Footer con info de autor */}
        <div
          className="pt-2 mt-1 border-top d-flex align-items-center justify-content-between"
          style={{ borderColor: isDark ? '#1e2c3a' : '#e2e8f0', fontSize: '0.74rem', color: isDark ? '#64748b' : '#94a3b8' }}
        >
          <span>ID: #{tag.idEtiqueta}</span>
          <span>{tag.vCreatedUser ? `Por: ${tag.vCreatedUser}` : ''}</span>
        </div>
      </div>
    </div>
  );
};

export default TagCard;
